import React, { createContext, useContext, useState, useEffect } from "react";
import { auth, database } from "../firebaseConfig";
import {
  createUserWithEmailAndPassword,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut,
} from "firebase/auth";
import { ref, set, get, child } from "firebase/database";

const AuthContext = createContext();

export const useAuth = () => {
  return useContext(AuthContext);
};

const VERSION = process.env.REACT_APP_VERSION || "dev";

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState(null);

  const formatFirebaseError = (error) => {
    if (!error || !error.code) return "An unknown error occurred.";
    const errorMessages = {
      "auth/email-already-in-use":
        "This email is already in use. Please log in or use a different email.",
      "auth/invalid-credential":
        "Invalid email or password. Please check your credentials and try again.",
      "auth/user-not-found":
        "No account found with this email. Please sign up or check the email entered.",
      "auth/wrong-password":
        "Incorrect password. Please try again or reset your password.",
      "auth/weak-password": "Password must be at least 6 characters.",
      "auth/invalid-email":
        "Invalid email format. Please enter a valid email address.",
      "auth/network-request-failed":
        "Network error. Please check your internet connection and try again.",
      "auth/too-many-requests":
        "Too many login attempts. Please wait and try again later.",
      "auth/operation-not-allowed":
        "This sign-in method is not enabled. Please contact support.",
    };

    return (
      errorMessages[error.code] ||
      "An unexpected authentication error occurred."
    );
  };
  useEffect(() => {
    setAuthError(null);

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        const userData = await getUserData(user.uid);
        setCurrentUser({ auth: user, data: userData });
      } else {
        setCurrentUser(null);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const register = async (email, password, displayName) => {
    try {
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        email,
        password
      );
      const user = userCredential.user;
      const userData = {
        email: user.email,
        created: new Date().toISOString(),
        displayName,
        game: {
          [VERSION]: {
            paid: false,
          },
        },
      };
      await set(ref(database, "users/" + user.uid), userData);
      setCurrentUser({ auth: user, data: userData });
    } catch (error) {
      setAuthError(formatFirebaseError(error));
    }
  };

  const login = async (email, password) => {
    try {
      await signInWithEmailAndPassword(auth, email, password);
    } catch (error) {
      setAuthError(formatFirebaseError(error));
    }
  };

  const logout = async () => {
    try {
      await signOut(auth);
    } catch (error) {
      setAuthError(formatFirebaseError(error));
    }
  };

  const getUserData = async (uid) => {
    try {
      const dbRef = ref(database);
      const snapshot = await get(child(dbRef, `users/${uid}`));
      if (snapshot.exists()) {
        return snapshot.val();
      } else {
        // logout();
      }
    } catch (error) {
      console.error("Error fetching user data:", error);
    }
  };

  const clearError = () => {
    setAuthError(null);
  };

  const value = {
    currentUser,
    register,
    login,
    logout,
    authError,
    clearError,
    getUserData,
    setAuthError,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
